/*
 * 戒导航 · 头像审核 —— 内容违规处置通知卡片
 *
 * 设计依据：doc/内容二次复核队列设计方案.md（v1.1）§4.8。
 *
 * 一句话职责：把「你的头像为什么被重置成默认了」这件事**告诉当事人**。
 *
 * 为什么可以直接读通知里的数据、不需要再请求后端：
 *   后端 ContentViolationBlueprint::getData() 里放的是**渲染所需的全部快照**
 *   （内容域 / 原因 / 重新上传地址）。这是刻意的 —— 记录行将来可能被清理，
 *   任何「再查一次记录」的做法都可能失败。
 *
 * ⚠️ 安全：note（管理员填写的复核备注）是**纯文本**，这里只作为文本节点渲染，
 *   绝不可用 m.trust / dangerouslySetInnerHTML（用户填写的备注内容不可信）。
 *
 * ⚠️ 字段契约：下方 ViolationContent 的字段名必须与后端
 *   JieDH\AvatarGuard\Notify\ContentViolationBlueprint::getData() 保持一致；
 *   后端改名时此处必须同步（后端文件里也有同款提醒）。
 *
 * ⚠️ JSX 写法约定（勿改成 import mithril）：
 *   本项目所有扩展都不 import mithril，JSX 由 flarum-webpack-config 的 babel
 *   以 `pragma: 'm'` 编译成对**全局标识符 `m`** 的调用。
 *
 * ⚠️ 图标选取红线（照 packages/review/src/forum/components/ModerationReceiptNotification.tsx）：
 *   本组件返回的是「通知类型」图标，与右侧动作按钮图标**同屏出现**
 *   （vendor/flarum/core/js/src/forum/components/HeaderListItem.tsx）。
 *   因此禁止使用 'fas fa-trash-alt' / 'fas fa-trash-can'（删除按钮语义，见 packages/notification）
 *   以及 'fas fa-check'（核心「标记已读」按钮图标）。此处选用用户/盾牌类图标，语义贴合且无冲突。
 *
 * 范本：packages/review/js/src/forum/components/ModerationReceiptNotification.tsx
 */
import app from 'flarum/forum/app';
import Notification from 'flarum/forum/components/Notification';

/**
 * 通知数据契约（= ContentViolationBlueprint::getData() 的返回值）。
 */

/**
 * 各内容域的通知图标。
 *
 * 新增内容域时在此登记；未登记的域回退到 DEFAULT_ICON（见下）。
 */
const ICONS = {
  avatar: 'fas fa-user-shield',
  post_text: 'fas fa-comment-slash',
  post_image: 'fas fa-image'
};

/** 未登记内容域的兜底图标（盾牌 = 合规处置语义，且与动作按钮图标无冲突）。 */
const DEFAULT_ICON = 'fas fa-shield-alt';
export default class ContentViolationNotification extends Notification {
  /**
   * 通知正文：按「内容域」取对应文案。
   *
   * 文案键形如 jiedh-avatar-guard.forum.notifications.content_violation_avatar_text，
   * 见 packages/avatar-guard/resources/locale/{zh-Hans,en}.yml。
   *
   * 未登记的域：Flarum 的 translator 会把 key 原样返回，此时回退到通用文案，
   * 避免界面上出现一串 key（照 packages/review 的处理思路）。
   */
  content() {
    const scope = this.data().scope || 'avatar';
    const key = `jiedh-avatar-guard.forum.notifications.content_violation_${scope}_text`;
    const text = app.translator.trans(key);
    return text === key ? app.translator.trans('jiedh-avatar-guard.forum.notifications.content_violation_text') : text;
  }
  icon() {
    return ICONS[this.data().scope] || DEFAULT_ICON;
  }

  /**
   * 点击通知跳转的地址。
   *
   * 头像场景 ⇒ 用户中心 /settings（在那里可以重新上传头像）。
   * 与后端 getData()['reuploadUrl'] 对应；为空时不生成链接（点击不高亮、不跳转）。
   */
  href() {
    const url = this.data().reuploadUrl;
    if (!url) return '';
    const base = app.forum.attribute('baseUrl') || '';

    // reuploadUrl 形如 '/settings'（站内相对路径），拼接 baseUrl 得到完整地址
    return `${base}${url}`;
  }

  /**
   * 副标题：展示「为什么被处置」。
   *
   * 没有原因时不显示该行（Flarum 的 HeaderListItem 会照常渲染空 div，视觉上无高度）。
   *
   * ⚠️ 必须用 div 包裹：父容器 .HeaderListItem-excerpt 是
   *   white-space: nowrap + overflow: hidden，靠内联元素是换不了行的。
   */
  excerpt() {
    const reason = this.reasonText();
    if (!reason) return '';
    return m("div", null, app.translator.trans('jiedh-avatar-guard.forum.notifications.reason', {
      reason
    }));
  }

  /**
   * 原因文案的兜底顺序：管理员自由文本（note）> 预设原因键（reasonCode）> 不显示。
   */
  reasonText() {
    const {
      note,
      reasonCode
    } = this.data();
    if (note) return note;
    if (reasonCode) {
      const key = `jiedh-avatar-guard.forum.notifications.reason_${reasonCode}`;
      const label = app.translator.trans(key);

      // 未登记的预设键：translator 原样返回 key，此时宁可不显示
      return label === key ? '' : String(label);
    }
    return '';
  }

  /**
   * 读取通知携带的数据（防御：后端数据结构异常时不抛错、不白屏）。
   */
  data() {
    const content = this.attrs.notification.content() || {};
    return {
      scope: String(content.scope || 'avatar'),
      reasonCode: content.reasonCode ? String(content.reasonCode) : null,
      note: content.note ? String(content.note) : null,
      reuploadUrl: content.reuploadUrl ? String(content.reuploadUrl) : ''
    };
  }
}
flarum.reg.add('jiedh-avatar-guard', 'forum/components/ContentViolationNotification', ContentViolationNotification);